import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Sparkles, MessageCircle, Heart, Star, Infinity } from 'lucide-react';

const Timeline: React.FC = () => {
    const { t } = useLanguage();

    const milestones = [
        { day: 1, title: t('timeline.day1'), icon: <Sparkles size={24} />, subtitle: 'Where everything quietly began' },
        { day: 10, title: t('timeline.day10'), icon: <MessageCircle size={24} />, subtitle: 'Conversations that made me smile' },
        { day: 20, title: t('timeline.day20'), icon: <Heart size={24} />, subtitle: 'A connection that slowly grew' },
        { day: 30, title: t('timeline.day30'), icon: <Star size={24} />, subtitle: 'Moments that felt natural and right' },
        { day: 40, title: t('timeline.day40'), icon: <Heart fill="currentColor" size={24} />, subtitle: 'You became my comfort and calm' },
        { day: 50, title: t('timeline.day50'), icon: <Heart fill="currentColor" size={24} />, subtitle: 'A small gift for a love that means everything' },
    ];

    return (
        <div className="px-6 py-12 max-w-6xl mx-auto">
            <div className="relative bg-white dark:bg-romantic-cardDark rounded-[3rem] p-8 md:p-12 shadow-2xl border border-primary-red/5">

                {/* Vertical Line */}
                <div className="absolute left-[2.75rem] md:left-[3.75rem] top-24 bottom-24 w-1 bg-gradient-to-b from-primary-red via-romantic-pink to-primary-red opacity-20" />

                {milestones.map((milestone, index) => (
                    <motion.div
                        key={milestone.day}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex mb-12 last:mb-0 relative"
                    >
                        {/* Timeline Icon */}
                        <div className="flex flex-col items-center mr-6 md:mr-10 relative z-10">
                            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary-red text-white flex items-center justify-center shadow-lg shadow-red-200 dark:shadow-none font-bold text-xl">
                                {milestone.day}
                            </div>
                        </div>

                        {/* Content Card */}
                        <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                                <span className="text-primary-red">{milestone.icon}</span>
                                <div>
                                    <h3 className="text-xl font-poppins font-bold text-primary-red">Day {milestone.day}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">{milestone.subtitle}</p>
                                </div>
                            </div>

                            <motion.div
                                whileHover={{ x: 5 }}
                                className="bg-primary-red/5 dark:bg-primary-red/10 p-5 rounded-2xl border border-primary-red/5 shadow-sm"
                            >
                                <p className="text-gray-700 dark:text-gray-200 leading-relaxed font-poppins">
                                    {milestone.title}
                                </p>
                            </motion.div>
                        </div>
                    </motion.div>
                ))}

                {/* Future/Infinity Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-20 pt-12 border-t border-gray-100 dark:border-gray-800 text-center"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-red text-white mb-6 shadow-xl shadow-red-100 dark:shadow-none">
                        <Infinity size={32} />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-playfair font-bold text-primary-red mb-4">Forever & Always</h2>
                    <p className="text-base text-gray-600 dark:text-gray-300 font-poppins max-w-xl mx-auto leading-relaxed">
                        These moments are just the beginning of our forever story.
                        Here's to infinite days of love, laughter, and endless adventures together. ❤️
                    </p>

                    <div className="flex justify-center space-x-6 mt-10 opacity-60">
                        <span>💖</span><span>✨</span><span>🌟</span><span>💫</span>
                    </div>
                </motion.div>
            </div>

            <p className="text-center mt-12 text-gray-400 font-poppins italic">
                "Every day with you is my favorite day."
            </p>
        </div>
    );
};

export default Timeline;
